<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import type { IUser } from '@/types/user.type';
import { getDateFormat } from '@/utils/formatDate';

const { t } = useI18n({ useScope: 'global' });

// ══ CUSTOMERS TAB ════════════════════════════════════════════════════════════

// Mode d'affichage de la liste : 'card' | 'table'
const viewMode = ref<'card' | 'table'>('card');

// ── Commerces (pour filtre) ────────────────────────────────────────────────────
const { data: restaurantsData } = useQuery({
  queryKey: ['ur-restaurants'],
  queryFn: async () => {
    const res = await axiosInstance.get(
      'restaurants/search?pagination=false&sortBy=name&order=ASC',
    );
    return res.data;
  },
  staleTime: 5 * 60_000,
});
const restaurants = computed<any[]>(() => {
  const d = restaurantsData.value as any;
  const list = d?.data ?? d?.restaurants ?? [];
  return Array.isArray(list) ? list : [];
});
function restaurantName(id: string | null | undefined) {
  if (!id) return '—';
  const r = restaurants.value.find((r: any) => r.id === id);
  return r ? (r.designation ?? r.name ?? id) : id;
}

const storeFilter = ref('');
const storeDropdownOpen = ref(false);
const storeSearch = ref('');
const filteredStores = computed(() =>
  restaurants.value.filter((r) =>
    (r.designation ?? r.name ?? '')
      .toLowerCase()
      .includes(storeSearch.value.toLowerCase()),
  ),
);
function selectStore(id: string) {
  storeFilter.value = id;
  storeDropdownOpen.value = false;
  storeSearch.value = '';
}
function clearStore() {
  storeFilter.value = '';
  storeSearch.value = '';
  storeDropdownOpen.value = false;
}
const selectedStoreName = computed(() =>
  storeFilter.value ? restaurantName(storeFilter.value) : '',
);

// Filter state
const userSearch = ref('');
const userPage = ref(1);
const PAGE_SIZES = [10, 20, 50] as const;
const userPageSize = ref<(typeof PAGE_SIZES)[number]>(20);

const sortBy = ref<'created' | 'updated' | 'order_count' | 'latest_order_date'>(
  'created',
);
const sortOrder = ref<'ASC' | 'DESC'>('DESC');

function toggleSortOrder() {
  sortOrder.value = sortOrder.value === 'DESC' ? 'ASC' : 'DESC';
}

watch([userSearch, sortBy, sortOrder, storeFilter, userPageSize], () => {
  userPage.value = 1;
});

const { data: usersData, isPending: usersLoading } = useQuery({
  queryKey: computed(() => [
    'ur-customers',
    userPage.value,
    userPageSize.value,
    userSearch.value,
    sortBy.value,
    sortOrder.value,
    storeFilter.value,
  ]),
  queryFn: async () => {
    const qs = new URLSearchParams();
    qs.append('pagination', 'true');
    qs.append('page', String(userPage.value));
    qs.append('pageSize', String(userPageSize.value));
    qs.append('sortBy', sortBy.value);
    qs.append('sortOrder', sortOrder.value);
    if (userSearch.value.trim()) qs.append('q', userSearch.value.trim());
    if (storeFilter.value) qs.append('restaurantId', storeFilter.value);
    const res = await axiosInstance.get<{ data: IUser[]; total: number }>(
      `customers/search?${qs.toString()}`,
    );
    return res.data;
  },
  staleTime: 60_000,
});

const users = computed<IUser[]>(() => usersData.value?.data ?? []);
const userTotal = computed(() => usersData.value?.total ?? 0);
const userTotalPages = computed(() =>
  Math.max(1, Math.ceil(userTotal.value / userPageSize.value)),
);
const userVisiblePages = computed(() => {
  const total = userTotalPages.value;
  const cur = userPage.value;
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const start = Math.max(1, Math.min(cur - 2, total - 4));
  return Array.from({ length: Math.min(5, total) }, (_, i) => start + i);
});

// ── Helpers ───────────────────────────────────────────────────────────────────
function userDisplayName(u: IUser) {
  const full = [u.firstname, u.lastname].filter(Boolean).join(' ').trim();
  return full || null;
}
function userInitials(u: IUser) {
  const name = userDisplayName(u);
  if (name) {
    const parts = name.split(' ');
    return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
  }
  return u.email ? u.email.slice(0, 2).toUpperCase() : null;
}
function isAnonymous(u: IUser) {
  return !userDisplayName(u) && !u.email && !u.phone;
}
function userColor(letter: string) {
  const colors = [
    '#3b82f6',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#06b6d4',
    '#f97316',
  ];
  return colors[letter.toUpperCase().charCodeAt(0) % colors.length];
}
</script>

<template>
  <div class="ur" @click="storeDropdownOpen = false">
    <!-- Header -->
    <div class="ur__header">
      <div>
        <h1 class="ur__title">{{ t('membresPage.title') }}</h1>
        <p class="ur__subtitle">{{ t('membresPage.subtitle') }}</p>
      </div>
    </div>

    <!-- ══ CUSTOMERS ══ -->
    <div class="ur__body">
      <!-- Filters bar -->
      <div class="ur__filters">
        <!-- Search -->
        <div class="ur__search-wrap">
          <i class="pi pi-search ur__search-icon" />
          <input
            v-model="userSearch"
            class="ur__search"
            :placeholder="t('membresPage.filters.searchPlaceholder')"
          />
        </div>

        <!-- Store filter dropdown -->
        <div class="ur__dropdown-wrap" @click.stop>
          <button
            class="ur__dropdown-btn"
            :class="{ 'ur__dropdown-btn--active': storeFilter }"
            @click="storeDropdownOpen = !storeDropdownOpen"
          >
            <i class="pi pi-building" />
            <span class="ur__dropdown-label">{{
              selectedStoreName || t('membresPage.filters.allStores')
            }}</span>
            <i
              v-if="storeFilter"
              class="pi pi-times ur__dropdown-clear"
              @click.stop="clearStore"
            />
            <i v-else class="pi pi-chevron-down ur__dropdown-arrow" />
          </button>
          <Transition name="ur-drop">
            <div v-if="storeDropdownOpen" class="ur__dropdown-panel">
              <div class="ur__dropdown-search-wrap">
                <i class="pi pi-search ur__dropdown-search-icon" />
                <input
                  v-model="storeSearch"
                  class="ur__dropdown-search"
                  :placeholder="t('membresPage.filters.searchStore')"
                  @click.stop
                />
              </div>
              <div class="ur__dropdown-list">
                <button
                  class="ur__dropdown-item"
                  :class="{ 'ur__dropdown-item--active': !storeFilter }"
                  @click="clearStore"
                >
                  {{ t('membresPage.filters.allStores') }}
                </button>
                <button
                  v-for="r in filteredStores"
                  :key="r.id"
                  class="ur__dropdown-item"
                  :class="{ 'ur__dropdown-item--active': storeFilter === r.id }"
                  @click="selectStore(r.id)"
                >
                  {{ r.designation ?? r.name }}
                </button>
              </div>
            </div>
          </Transition>
        </div>

        <select v-model="sortBy" class="ur__sort-select">
          <option value="created">
            {{ t('membresPage.filters.sortCreated') }}
          </option>
          <option value="updated">
            {{ t('membresPage.filters.sortUpdated') }}
          </option>
          <option value="order_count">
            {{ t('membresPage.filters.sortOrderCount') }}
          </option>
          <option value="latest_order_date">
            {{ t('membresPage.filters.sortLatestOrder') }}
          </option>
        </select>

        <button
          class="ur__sort-btn"
          :title="t('membresPage.filters.toggleOrder')"
          @click="toggleSortOrder"
        >
          <i
            :class="
              sortOrder === 'DESC'
                ? 'pi pi-sort-amount-down'
                : 'pi pi-sort-amount-up'
            "
          />
        </button>

        <select v-model.number="userPageSize" class="ur__page-size-select">
          <option v-for="ps in PAGE_SIZES" :key="ps" :value="ps">
            {{ ps }} {{ t('membresPage.filters.perPage') }}
          </option>
        </select>

        <span class="ur__count">{{
          t('membresPage.pagination.results', { total: userTotal })
        }}</span>

        <div class="ur__view-toggle">
          <button
            class="ur__view-btn"
            :class="{ 'ur__view-btn--active': viewMode === 'card' }"
            :title="t('membresPage.filters.viewCards')"
            @click="viewMode = 'card'"
          >
            <i class="pi pi-th-large" />
          </button>
          <button
            class="ur__view-btn"
            :class="{ 'ur__view-btn--active': viewMode === 'table' }"
            :title="t('membresPage.filters.viewTable')"
            @click="viewMode = 'table'"
          >
            <i class="pi pi-list" />
          </button>
        </div>
      </div>

      <!-- Cards -->
      <div v-if="viewMode === 'card'" class="ur__cards-wrap">
        <div v-if="usersLoading" class="ur__cards">
          <div
            v-for="i in 8"
            :key="i"
            class="ur__card-item ur__card-item--sk"
          />
        </div>
        <div v-else-if="!users.length" class="ur__empty">
          <i class="pi pi-users" />
          <span>{{ t('membresPage.table.empty') }}</span>
        </div>
        <div v-else class="ur__cards">
          <div v-for="u in users" :key="u.id" class="ur__card-item">
            <div
              class="ur__avatar"
              :class="{ 'ur__avatar--anon': isAnonymous(u) }"
              :style="{
                background: isAnonymous(u)
                  ? '#64748b'
                  : userColor((userInitials(u) ?? 'A')[0]),
              }"
            >
              <i
                v-if="isAnonymous(u)"
                class="pi pi-user"
                style="font-size: 1.1rem"
              />
              <span v-else>{{ userInitials(u) }}</span>
            </div>
            <span class="ur__card-item-name">{{
              userDisplayName(u) ?? t('membresPage.table.anonymous')
            }}</span>
            <span v-if="u.email" class="ur__card-item-sub">{{ u.email }}</span>
            <span v-else-if="u.phone" class="ur__card-item-sub">{{
              u.phone
            }}</span>
            <span v-else class="ur__card-item-sub"
              >ID · {{ u.id.slice(0, 8) }}</span
            >

            <div class="ur__card-item-meta">
              <span
                class="ur__status-dot"
                :class="
                  u.disabledAt || u.lockedAt
                    ? 'ur__status-dot--off'
                    : 'ur__status-dot--on'
                "
              />
              <span class="ur__status-label">{{
                u.disabledAt || u.lockedAt
                  ? t('membresPage.table.inactive')
                  : t('membresPage.table.active')
              }}</span>
              <span class="ur__card-item-lang">{{
                (u as any).language?.toUpperCase() ?? '—'
              }}</span>
            </div>
            <span class="ur__card-item-date">{{
              getDateFormat(u.createdAt)
            }}</span>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div v-else class="ur__table-wrap">
        <table class="ur__table">
          <thead>
            <tr>
              <th>{{ t('membresPage.table.member') }}</th>
              <th>{{ t('membresPage.table.language') }}</th>
              <th>{{ t('membresPage.table.status') }}</th>
              <th>{{ t('membresPage.table.createdAt') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="usersLoading">
              <td colspan="4" class="ur__state-cell">
                <i class="pi pi-spin pi-spinner" />
              </td>
            </tr>
            <tr v-else-if="!users.length">
              <td colspan="4" class="ur__state-cell ur__state-cell--empty">
                Aucun membre trouvé
              </td>
            </tr>
            <tr v-for="u in users" :key="u.id" class="ur__row">
              <td>
                <div class="ur__user-cell">
                  <div
                    class="ur__avatar"
                    :class="{ 'ur__avatar--anon': isAnonymous(u) }"
                    :style="{
                      background: isAnonymous(u)
                        ? '#64748b'
                        : userColor((userInitials(u) ?? 'A')[0]),
                    }"
                  >
                    <i
                      v-if="isAnonymous(u)"
                      class="pi pi-user"
                      style="font-size: 0.9rem"
                    />
                    <span v-else>{{ userInitials(u) }}</span>
                  </div>
                  <div class="ur__user-info">
                    <span class="ur__user-name">{{
                      userDisplayName(u) ?? t('membresPage.table.anonymous')
                    }}</span>
                    <span v-if="u.email" class="ur__user-email">{{
                      u.email
                    }}</span>
                    <span v-else-if="u.phone" class="ur__user-email">{{
                      u.phone
                    }}</span>
                    <span v-else class="ur__user-anon"
                      >ID · {{ u.id.slice(0, 8) }}</span
                    >
                  </div>
                </div>
              </td>
              <td class="ur__cell-muted">
                {{ (u as any).language?.toUpperCase() ?? '—' }}
              </td>
              <td>
                <span
                  class="ur__status-dot"
                  :class="
                    u.disabledAt || u.lockedAt
                      ? 'ur__status-dot--off'
                      : 'ur__status-dot--on'
                  "
                />
                <span class="ur__status-label">{{
                  u.disabledAt || u.lockedAt
                    ? t('membresPage.table.inactive')
                    : t('membresPage.table.active')
                }}</span>
              </td>
              <td class="ur__cell-muted">{{ getDateFormat(u.createdAt) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="userTotalPages > 1 || userTotal > 0" class="ur__pagination">
        <button
          class="ur__page-btn"
          :disabled="userPage === 1"
          @click="userPage--"
        >
          <i class="pi pi-chevron-left" />
        </button>
        <button
          v-for="p in userVisiblePages"
          :key="p"
          class="ur__page-btn"
          :class="{ 'ur__page-btn--active': p === userPage }"
          @click="userPage = p"
        >
          {{ p }}
        </button>
        <button
          class="ur__page-btn"
          :disabled="userPage === userTotalPages"
          @click="userPage++"
        >
          <i class="pi pi-chevron-right" />
        </button>
        <span class="ur__page-info"
          >{{ userPage }}/{{ userTotalPages }} ·
          {{ t('membresPage.pagination.results', { total: userTotal }) }}</span
        >
      </div>
    </div>
  </div>
</template>

<style scoped>
.ur {
  padding: 2.5rem 3rem;
  min-height: calc(100vh - 5rem);
  display: flex;
  flex-direction: column;
  gap: 0;
}
.ur__header {
  margin-bottom: 1.75rem;
}
.ur__title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0b1320;
  margin: 0 0 0.25rem;
}
.ur__subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

/* Tabs */
.ur__tabs {
  display: flex;
  gap: 0.25rem;
  border-bottom: 2px solid rgba(0, 0, 0, 0.06);
  margin-bottom: 1.5rem;
}
.ur__tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.15s;
}
.ur__tab:hover {
  color: #0b1320;
}
.ur__tab--active {
  color: #0b1320;
  border-bottom-color: #0b1320;
}
.ur__tab-count {
  background: #0b1320;
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  border-radius: 9999px;
  padding: 0.1rem 0.45rem;
}

.ur__body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* ── Filters bar ──────────────────────────────────────────────────────────── */
.ur__filters {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}

.ur__search-wrap {
  position: relative;
  flex: 1;
  min-width: 12rem;
  max-width: 22rem;
}
.ur__search-icon {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.875rem;
  pointer-events: none;
}
.ur__search {
  width: 100%;
  padding: 0.625rem 1rem 0.625rem 2.375rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.75rem;
  font-size: 0.875rem;
  background: #fff;
  color: #0b1320;
  outline: none;
  font-family: inherit;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}
.ur__search:focus {
  border-color: #0b1320;
  box-shadow: 0 0 0 3px rgba(11, 19, 32, 0.08);
}

/* Dropdown filter */
.ur__dropdown-wrap {
  position: relative;
  flex-shrink: 0;
}
.ur__dropdown-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  font-family: inherit;
  transition:
    border-color 0.15s,
    background 0.15s;
  white-space: nowrap;
  max-width: 13rem;
}
.ur__dropdown-btn:hover {
  border-color: #0b1320;
}
.ur__dropdown-btn--active {
  border-color: #0b1320;
  background: rgba(11, 19, 32, 0.04);
  color: #0b1320;
}
.ur__dropdown-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.ur__dropdown-arrow {
  font-size: 0.625rem;
  color: #94a3b8;
  transition: transform 0.2s;
  flex-shrink: 0;
}
.ur__dropdown-arrow--open {
  transform: rotate(180deg);
}
.ur__dropdown-clear {
  font-size: 0.625rem;
  color: #94a3b8;
  flex-shrink: 0;
  transition: color 0.15s;
}
.ur__dropdown-clear:hover {
  color: #ef4444;
}
.ur__dropdown-panel {
  position: absolute;
  top: calc(100% + 0.375rem);
  left: 0;
  min-width: 15rem;
  max-width: 22rem;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.875rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  z-index: 50;
  overflow: hidden;
}
.ur__dropdown-search-wrap {
  position: relative;
  padding: 0.625rem 0.625rem 0;
}
.ur__dropdown-search-icon {
  position: absolute;
  left: 1.125rem;
  top: 50%;
  transform: translateY(-30%);
  color: #94a3b8;
  font-size: 0.75rem;
  pointer-events: none;
}
.ur__dropdown-search {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  background: rgba(0, 0, 0, 0.03);
  color: #0b1320;
  outline: none;
  font-family: inherit;
  box-sizing: border-box;
}
.ur__dropdown-search:focus {
  border-color: #0b1320;
}
.ur__dropdown-list {
  max-height: 14rem;
  overflow-y: auto;
  padding: 0.375rem;
  scrollbar-width: thin;
}
.ur__dropdown-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: none;
  background: none;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  color: #374151;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: background 0.12s;
}
.ur__dropdown-item:hover {
  background: rgba(0, 0, 0, 0.05);
}
.ur__dropdown-item--active {
  background: rgba(11, 19, 32, 0.07);
  color: #0b1320;
  font-weight: 700;
}
.ur__dropdown-item-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ur__dropdown-item-code {
  font-size: 0.6875rem;
  color: #94a3b8;
  white-space: nowrap;
  flex-shrink: 0;
}
.ur__dropdown-empty {
  padding: 0.75rem;
  text-align: center;
  font-size: 0.8125rem;
  color: #94a3b8;
}

/* Active filter chips */
.ur__active-filters {
  display: flex;
  gap: 0.375rem;
  flex-wrap: wrap;
  align-items: center;
}
.ur__filter-chip {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.3rem 0.5rem 0.3rem 0.75rem;
  background: rgba(11, 19, 32, 0.07);
  border: 1px solid rgba(11, 19, 32, 0.15);
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #0b1320;
}
.ur__filter-chip button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: #374151;
  cursor: pointer;
  font-size: 0.5rem;
}
.ur__filter-chip button:hover {
  background: rgba(0, 0, 0, 0.2);
}

/* Sort select + toggle order */
.ur__sort-select,
.ur__page-size-select {
  height: 2.5rem;
  padding: 0 2rem 0 0.875rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.75rem;
  background: #fff;
  color: #0b1320;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  flex-shrink: 0;
}
.ur__count {
  margin-left: auto;
  font-size: 0.8125rem;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
}
.ur__sort-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.75rem;
  background: #fff;
  color: #374151;
  font-size: 0.8125rem;
  cursor: pointer;
  flex-shrink: 0;
}
.ur__sort-btn:hover {
  border-color: #ffd60a;
}

/* View toggle */
.ur__view-toggle {
  display: flex;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.75rem;
  overflow: hidden;
  flex-shrink: 0;
}
.ur__view-btn {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #fff;
  color: #374151;
  font-size: 0.875rem;
  cursor: pointer;
}
.ur__view-btn--active {
  background: #0b1320;
  color: #fff;
}

/* Cards */
.ur__cards-wrap {
  margin-top: 1.25rem;
}
.ur__cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1.25rem;
}
.ur__card-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
  text-align: center;
  transition:
    box-shadow 0.3s cubic-bezier(0.2, 1, 0.3, 1),
    transform 0.3s cubic-bezier(0.2, 1, 0.3, 1);
}
.ur__card-item:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}
.ur__card-item--sk {
  height: 13rem;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.03) 25%,
    rgba(0, 0, 0, 0.06) 50%,
    rgba(0, 0, 0, 0.03) 75%
  );
  background-size: 200% 100%;
  animation: ur-shimmer 1.4s infinite;
}
@keyframes ur-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.ur__card-item-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0b1320;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.ur__card-item-sub {
  font-size: 0.75rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.ur__card-item-meta {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.375rem;
}
.ur__card-item-lang {
  font-size: 0.6875rem;
  font-weight: 700;
  color: #6b7280;
  margin-left: 0.375rem;
  padding-left: 0.375rem;
  border-left: 1px solid rgba(0, 0, 0, 0.1);
}
.ur__card-item-date {
  font-size: 0.6875rem;
  color: #9ca3af;
}
.ur__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 4rem 1rem;
  color: #6b7280;
}
.ur__empty i {
  font-size: 2rem;
  opacity: 0.4;
}

.ur__add-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: #ffd60a;
  color: #0b1320;
  border: none;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
  white-space: nowrap;
  margin-left: auto;
}
.ur__add-btn:hover {
  background: #e6c109;
}

/* Table */
.ur__table-wrap {
  overflow-x: auto;
  border-radius: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.07);
  background: rgba(255, 255, 255, 0.85);
}
.ur__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.ur__table thead tr {
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
}
.ur__table th {
  padding: 0.875rem 1rem;
  text-align: left;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
  white-space: nowrap;
}
.ur__table td {
  padding: 0.875rem 1rem;
  color: #0b1320;
  border-top: 1px solid rgba(0, 0, 0, 0.04);
  vertical-align: middle;
}
.ur__state-cell {
  padding: 3rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.875rem;
}
.ur__state-cell--empty {
  color: #b0bec5;
}
.ur__row {
  cursor: pointer;
  transition: background 0.12s;
}
.ur__row:hover {
  background: rgba(11, 19, 32, 0.025);
}
.ur__row--selected {
  background: rgba(255, 214, 10, 0.07);
}

.ur__user-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.ur__avatar {
  width: 2.375rem;
  height: 2.375rem;
  border-radius: 50%;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.03em;
}
.ur__user-info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.ur__user-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0b1320;
}
.ur__user-email {
  font-size: 0.75rem;
  color: #6b7280;
}
.ur__user-anon {
  font-size: 0.7rem;
  color: #94a3b8;
  font-family: 'Courier New', monospace;
}
.ur__cell-muted {
  color: #6b7280;
  font-size: 0.8125rem;
}
.ur__roles-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  align-items: center;
}
.ur__role-chip {
  padding: 0.15rem 0.55rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 700;
  border: 1px solid;
  white-space: nowrap;
}
.ur__role-chip--none {
  background: rgba(148, 163, 184, 0.1) !important;
  color: #94a3b8 !important;
  border-color: rgba(148, 163, 184, 0.2) !important;
  font-style: italic;
}
.ur__role-more {
  font-size: 0.6875rem;
  color: #94a3b8;
}
.ur__store-pill {
  display: inline-block;
  padding: 0.15rem 0.55rem;
  background: rgba(11, 19, 32, 0.06);
  border: 1px solid rgba(11, 19, 32, 0.1);
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}
.ur__store-pill + .ur__store-pill {
  margin-left: 0.3rem;
}
.ur__store-none {
  font-size: 0.8125rem;
  color: #94a3b8;
  font-style: italic;
}
.ur__status-dot {
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  margin-right: 0.375rem;
}
.ur__status-dot--on {
  background: #10b981;
}
.ur__status-dot--off {
  background: #ef4444;
}
.ur__status-label {
  font-size: 0.8125rem;
  color: #374151;
}
.ur__row-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.04);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: background 0.15s;
}
.ur__row-btn:hover {
  background: rgba(0, 0, 0, 0.08);
  color: #0b1320;
}

/* Pagination */
.ur__pagination {
  display: flex;
  gap: 0.35rem;
  align-items: center;
  justify-content: center;
  padding-top: 0.25rem;
}
.ur__page-btn {
  min-width: 2.25rem;
  height: 2.25rem;
  padding: 0 0.5rem;
  border-radius: 0.625rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #fff;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.ur__page-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.05);
}
.ur__page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.ur__page-btn--active {
  background: #0b1320;
  color: #fff;
  border-color: #0b1320;
  font-weight: 700;
}
.ur__page-info {
  font-size: 0.8125rem;
  color: #94a3b8;
  margin-left: 0.5rem;
}

/* Roles grid */
.ur__roles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(20rem, 1fr));
  gap: 1rem;
}
.ur__role-card {
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 1rem;
  padding: 1.25rem;
  cursor: pointer;
  transition:
    box-shadow 0.2s,
    border-color 0.2s;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.ur__role-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 0, 0, 0.14);
}
.ur__role-card-head {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}
.ur__role-card-info {
  flex: 1;
  min-width: 0;
}
.ur__role-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}
.ur__role-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0b1320;
}
.ur__role-code {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.1rem;
}
.ur__role-status {
  padding: 0.2rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
}
.ur__role-status--on {
  background: #d1fae5;
  color: #065f46;
}
.ur__role-status--off {
  background: #fee2e2;
  color: #991b1b;
}
.ur__role-desc {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
}
.ur__role-perms {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}
.ur__perm-chip {
  padding: 0.15rem 0.5rem;
  background: rgba(11, 19, 32, 0.06);
  border-radius: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #374151;
}
.ur__perm-more {
  font-size: 0.6875rem;
  color: #94a3b8;
}

/* ══ Formulaire création ══════════════════════════════════════════════════════ */
.uf {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 1.125rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.07);
  overflow: hidden;
}
.uf__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.125rem 1.375rem;
  background: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.uf__avatar-preview {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.0625rem;
  font-weight: 700;
  flex-shrink: 0;
  letter-spacing: 0.03em;
}
.uf__header-text {
  flex: 1;
}
.uf__header-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0b1320;
  margin: 0 0 0.125rem;
}
.uf__header-sub {
  font-size: 0.775rem;
  color: #6b7280;
  margin: 0;
}
.uf__close {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: background 0.15s;
}
.uf__close:hover {
  background: rgba(0, 0, 0, 0.07);
}
.uf__body {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}
.uf__section {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.uf__section-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
  margin: 0;
}
.uf__sep {
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
}
.uf__grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}
@media (max-width: 520px) {
  .uf__grid2 {
    grid-template-columns: 1fr;
  }
}
.uf__field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.uf__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
}
.uf__required {
  color: #ef4444;
}
.uf__input {
  width: 100%;
  padding: 0.5625rem 0.875rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0.625rem;
  font-size: 0.875rem;
  color: #0b1320;
  background: #fff;
  font-family: inherit;
  outline: none;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  box-sizing: border-box;
}
.uf__input:focus {
  border-color: #0b1320;
  box-shadow: 0 0 0 3px rgba(11, 19, 32, 0.07);
}
.uf__input::placeholder {
  color: #94a3b8;
}
.uf__pwd-wrap {
  display: flex;
  gap: 0.375rem;
  align-items: stretch;
}
.uf__input--pwd {
  flex: 1;
}
.uf__pwd-eye {
  flex-shrink: 0;
  width: 2.375rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0.625rem;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: border-color 0.15s;
}
.uf__pwd-eye:hover {
  border-color: #0b1320;
  color: #0b1320;
}
.uf__pwd-gen {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0.625rem;
  background: #fff;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  font-family: inherit;
  transition:
    border-color 0.15s,
    background 0.15s;
  white-space: nowrap;
}
.uf__pwd-gen:hover {
  border-color: #0b1320;
  background: #f8f9fb;
}
.uf__pwd-preview {
  display: block;
  margin-top: 0.375rem;
  font-family: 'Courier New', monospace;
  font-size: 0.8125rem;
  color: #059669;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 0.5rem;
  padding: 0.375rem 0.75rem;
  word-break: break-all;
}
.uf__error {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8125rem;
  color: #ef4444;
  margin: 0.25rem 0 0;
}
.uf__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 0.375rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  margin-top: 0.375rem;
}
.uf__btn-cancel {
  padding: 0.5625rem 1.125rem;
  background: rgba(0, 0, 0, 0.05);
  color: #374151;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
}
.uf__btn-cancel:hover {
  background: rgba(0, 0, 0, 0.09);
}
.uf__btn-submit {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5625rem 1.25rem;
  background: #0b1320;
  color: #fff;
  border: none;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s;
}
.uf__btn-submit:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.uf__btn-submit:not(:disabled):hover {
  opacity: 0.88;
}

/* Panel */
.ur-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 100;
  display: flex;
  justify-content: flex-end;
}
.ur-panel {
  width: min(34rem, 100vw);
  height: 100%;
  background: #f8f9fb;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ur-panel__head {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  background: #fff;
  flex-shrink: 0;
}
.ur-panel__avatar {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ur-panel__head-info {
  flex: 1;
  min-width: 0;
}
.ur-panel__name {
  font-size: 1.0625rem;
  font-weight: 700;
  color: #0b1320;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ur-panel__email {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0.1rem 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ur-panel__close {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(0, 0, 0, 0.04);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: background 0.15s;
  flex-shrink: 0;
}
.ur-panel__close:hover {
  background: rgba(0, 0, 0, 0.08);
  color: #0b1320;
}
.ur-panel__body {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem 1.75rem;
  scrollbar-width: thin;
}
.ur-panel__section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.ur-panel__section-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0b1320;
  margin: 0;
}
.ur-panel__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
}
.ur-panel__cell {
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 0.875rem;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.ur-panel__cell--full {
  grid-column: 1/-1;
}
.ur-panel__cell-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
}
.ur-panel__cell-val {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0b1320;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
}
.ur-panel__role-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.ur-panel__role-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
}
.ur-panel__role-item-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.ur-panel__role-store {
  font-size: 0.75rem;
  color: #94a3b8;
}
.ur-panel__role-remove {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(239, 68, 68, 0.2);
  background: rgba(239, 68, 68, 0.06);
  color: #ef4444;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  transition: background 0.15s;
  flex-shrink: 0;
}
.ur-panel__role-remove:hover {
  background: rgba(239, 68, 68, 0.14);
}
.ur-panel__empty {
  font-size: 0.8125rem;
  color: #94a3b8;
  margin: 0;
}
.ur-panel__desc-text {
  font-size: 0.875rem;
  color: #374151;
  margin: 0;
}
.ur-panel__perms-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}
.ur-panel__edit-form {
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 0.875rem;
  padding: 1rem 1.25rem;
}
.ur-panel__danger-zone {
  border-top: 1px dashed rgba(239, 68, 68, 0.2);
  padding-top: 1rem;
}
.ur-panel__danger-title {
  color: #ef4444 !important;
}
.ur-panel__del-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5625rem 1.125rem;
  background: rgba(239, 68, 68, 0.06);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
  align-self: flex-start;
}
.ur-panel__del-btn:hover {
  background: rgba(239, 68, 68, 0.14);
}
.ur-panel__confirm-delete {
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 0.875rem;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.ur-panel__confirm-msg {
  font-size: 0.875rem;
  color: #374151;
  margin: 0;
  font-weight: 500;
}
.ur-panel__confirm-actions {
  display: flex;
  gap: 0.75rem;
}
.ur-panel__cancel-del {
  padding: 0.5rem 1rem;
  background: rgba(0, 0, 0, 0.05);
  color: #374151;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.ur-panel__do-del {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  background: #ef4444;
  color: #fff;
  border: none;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s;
}
.ur-panel__do-del:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal */
.ur-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 110;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}
.ur-modal {
  width: min(28rem, 100%);
  background: #fff;
  border-radius: 1.25rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}
.ur-modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
}
.ur-modal__title {
  font-size: 1.0625rem;
  font-weight: 700;
  color: #0b1320;
  margin: 0;
}
.ur-modal__body {
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.ur-modal__textarea {
  resize: vertical;
  min-height: 4rem;
}
.ur-modal__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.07);
}

/* Toast */
.ur-toast {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 200;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  border-radius: 0.875rem;
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  min-width: 16rem;
  max-width: 28rem;
}
.ur-toast--success {
  background: #0b1320;
  color: #fff;
}
.ur-toast--error {
  background: #ef4444;
  color: #fff;
}
.ur-toast__close {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  margin-left: auto;
  flex-shrink: 0;
}

/* Transitions */
.ur-panel-enter-active,
.ur-panel-leave-active {
  transition: opacity 0.25s ease;
}
.ur-panel-enter-active .ur-panel,
.ur-panel-leave-active .ur-panel {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.ur-panel-enter-from,
.ur-panel-leave-to {
  opacity: 0;
}
.ur-panel-enter-from .ur-panel,
.ur-panel-leave-to .ur-panel {
  transform: translateX(100%);
}
.ur-fade-enter-active,
.ur-fade-leave-active {
  transition: opacity 0.2s;
}
.ur-fade-enter-from,
.ur-fade-leave-to {
  opacity: 0;
}
.ur-slide-enter-active,
.ur-slide-leave-active {
  transition:
    opacity 0.2s,
    transform 0.2s;
}
.ur-slide-enter-from,
.ur-slide-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem);
}
.ur-toast-enter-active,
.ur-toast-leave-active {
  transition:
    opacity 0.25s,
    transform 0.25s;
}
.ur-toast-enter-from,
.ur-toast-leave-to {
  opacity: 0;
  transform: translateY(0.75rem);
}
.ur-drop-enter-active,
.ur-drop-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.ur-drop-enter-from,
.ur-drop-leave-to {
  opacity: 0;
  transform: translateY(-0.25rem);
}

@media (max-width: 1023px) {
  .ur {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .ur {
    padding: 1rem;
  }
  .ur__tabs {
    overflow-x: auto;
  }
}

/* ══ Dark mode ════════════════════════════════════════════════════════════════ */
[data-theme='dark'] .ur__title {
  color: #f1f5f9;
}
[data-theme='dark'] .ur__subtitle {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .ur__tabs {
  border-bottom-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .ur__tab {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .ur__tab:hover {
  color: #f1f5f9;
}
[data-theme='dark'] .ur__tab--active {
  color: #ffd60a;
  border-bottom-color: #ffd60a;
}
[data-theme='dark'] .ur__tab-count {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .ur__search {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: #f1f5f9;
}
[data-theme='dark'] .ur__search:focus {
  border-color: #ffd60a;
}
[data-theme='dark'] .ur__search::placeholder {
  color: rgba(255, 255, 255, 0.25);
}
[data-theme='dark'] .ur__dropdown-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.65);
}
[data-theme='dark'] .ur__dropdown-btn:hover {
  border-color: rgba(255, 255, 255, 0.2);
}
[data-theme='dark'] .ur__dropdown-btn--active {
  background: rgba(255, 214, 10, 0.08);
  border-color: rgba(255, 214, 10, 0.3);
  color: #ffd60a;
}
[data-theme='dark'] .ur__dropdown-panel {
  background: #1e2130;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}
[data-theme='dark'] .ur__dropdown-search {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: #f1f5f9;
}
[data-theme='dark'] .ur__dropdown-search:focus {
  border-color: #ffd60a;
}
[data-theme='dark'] .ur__dropdown-item {
  color: rgba(255, 255, 255, 0.7);
}
[data-theme='dark'] .ur__dropdown-item:hover {
  background: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .ur__dropdown-item--active {
  background: rgba(255, 214, 10, 0.1);
  color: #ffd60a;
}
[data-theme='dark'] .ur__dropdown-item-code {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .ur__filter-chip {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.12);
  color: #f1f5f9;
}
[data-theme='dark'] .ur__filter-chip button {
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .ur__sort-select,
[data-theme='dark'] .ur__page-size-select,
[data-theme='dark'] .ur__sort-btn,
[data-theme='dark'] .ur__view-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .ur__count {
  color: rgba(255, 255, 255, 0.45);
}
[data-theme='dark'] .ur__view-btn--active {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .ur__card-item {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
[data-theme='dark'] .ur__card-item:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
}
[data-theme='dark'] .ur__card-item-name {
  color: #f1f5f9;
}
[data-theme='dark'] .ur__card-item-sub,
[data-theme='dark'] .ur__card-item-date {
  color: rgba(255, 255, 255, 0.45);
}
[data-theme='dark'] .ur__card-item-lang {
  color: rgba(255, 255, 255, 0.45);
  border-left-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .ur__empty {
  color: rgba(255, 255, 255, 0.45);
}
[data-theme='dark'] .ur__add-btn {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .ur__table-wrap {
  background: rgba(20, 22, 28, 0.7);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .ur__table th {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .ur__table td {
  color: #f1f5f9;
  border-top-color: rgba(255, 255, 255, 0.04);
}
[data-theme='dark'] .ur__row:hover {
  background: rgba(255, 255, 255, 0.04);
}
[data-theme='dark'] .ur__row--selected {
  background: rgba(255, 214, 10, 0.06);
}
[data-theme='dark'] .ur__user-name {
  color: #f1f5f9;
}
[data-theme='dark'] .ur__user-email {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .ur__user-anon {
  color: rgba(255, 255, 255, 0.25);
}
[data-theme='dark'] .ur__cell-muted {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .ur__status-label {
  color: rgba(255, 255, 255, 0.65);
}
[data-theme='dark'] .ur__store-pill {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.65);
}
[data-theme='dark'] .ur__store-none {
  color: rgba(255, 255, 255, 0.25);
}
[data-theme='dark'] .ur__row-btn {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .ur__row-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .ur__page-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.65);
}
[data-theme='dark'] .ur__page-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .ur__page-btn--active {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}
[data-theme='dark'] .ur__page-info {
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .ur__role-card {
  background: rgba(20, 22, 28, 0.7);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .ur__role-card:hover {
  border-color: rgba(255, 214, 10, 0.2);
}
[data-theme='dark'] .ur__role-name {
  color: #f1f5f9;
}
[data-theme='dark'] .ur__role-code {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .ur__role-desc {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .ur__perm-chip {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.65);
}
[data-theme='dark'] .uf {
  background: rgba(20, 22, 28, 0.92);
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .uf__header {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .uf__header-title {
  color: #f1f5f9;
}
[data-theme='dark'] .uf__header-sub {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .uf__close {
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .uf__close:hover {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .uf__section-label {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .uf__sep {
  background: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .uf__label {
  color: rgba(255, 255, 255, 0.65);
}
[data-theme='dark'] .uf__input {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  color: #f1f5f9;
}
[data-theme='dark'] .uf__input:focus {
  border-color: #ffd60a;
  box-shadow: 0 0 0 3px rgba(255, 214, 10, 0.08);
}
[data-theme='dark'] .uf__input::placeholder {
  color: rgba(255, 255, 255, 0.25);
}
[data-theme='dark'] .uf__input option {
  background: #1a1d26;
}
[data-theme='dark'] .uf__pwd-eye {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .uf__pwd-eye:hover {
  border-color: #ffd60a;
  color: #ffd60a;
}
[data-theme='dark'] .uf__pwd-gen {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.65);
}
[data-theme='dark'] .uf__pwd-gen:hover {
  border-color: #ffd60a;
  background: rgba(255, 255, 255, 0.09);
}
[data-theme='dark'] .uf__actions {
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .uf__btn-cancel {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.65);
}
[data-theme='dark'] .uf__btn-cancel:hover {
  background: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .uf__btn-submit {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .ur-panel {
  background: rgba(14, 16, 20, 0.98);
}
[data-theme='dark'] .ur-panel__head {
  background: rgba(20, 22, 28, 0.9);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .ur-panel__name {
  color: #f1f5f9;
}
[data-theme='dark'] .ur-panel__email {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .ur-panel__close {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .ur-panel__close:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}
[data-theme='dark'] .ur-panel__section-title {
  color: #f1f5f9;
}
[data-theme='dark'] .ur-panel__cell {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .ur-panel__cell-label {
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .ur-panel__cell-val {
  color: #f1f5f9;
}
[data-theme='dark'] .ur-panel__role-item {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .ur-panel__role-store {
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .ur-panel__empty {
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .ur-panel__desc-text {
  color: rgba(255, 255, 255, 0.55);
}
[data-theme='dark'] .ur-panel__edit-form {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .ur-panel__danger-zone {
  border-color: rgba(239, 68, 68, 0.15);
}
[data-theme='dark'] .ur-panel__del-btn {
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.2);
}
[data-theme='dark'] .ur-panel__del-btn:hover {
  background: rgba(239, 68, 68, 0.16);
}
[data-theme='dark'] .ur-panel__confirm-delete {
  background: rgba(239, 68, 68, 0.06);
  border-color: rgba(239, 68, 68, 0.15);
}
[data-theme='dark'] .ur-panel__confirm-msg {
  color: rgba(255, 255, 255, 0.7);
}
[data-theme='dark'] .ur-panel__cancel-del {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.65);
}
[data-theme='dark'] .ur-panel__perms-wrap .ur__perm-chip {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.65);
}
[data-theme='dark'] .ur-modal {
  background: rgba(20, 22, 28, 0.98);
}
[data-theme='dark'] .ur-modal__head {
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .ur-modal__title {
  color: #f1f5f9;
}
[data-theme='dark'] .ur-modal__foot {
  border-color: rgba(255, 255, 255, 0.06);
}
</style>
